/** Importing {@link WidgetInstance} into VS Code makes the whole experience a bit laggy. Sorry... */
import { WidgetInstance } from 'friendly-challenge';
import { sendNotification } from '../../ts/helper';

interface FreeFormReadyEvent extends SubmitEvent {
  freeform: any;
  form: HTMLFormElement;
}

export const basisForm = () => {
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll('[is="basis-form"]')
  );

  components.forEach((component) => {
    const form = component.querySelector('form');

    /**
     * Depending on what happens with the form, its state may change. For example a form could fail to submit or a submission could be successfull. We can react to the state changes. We only react to the last mutation in a row ({@link timer}).
     */
    const handleStateChanges = () => {
      /** A timer for the mutation observer that ensures, we trigger only on the last mutation, not on every mutation. The timer is reset after `100` ms. */
      let timer: NodeJS.Timeout;

      const callback = (mutationList) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList') {
              /**
               * Do stuff when the form is submitted successfully
               */
              const handleSuccessMessage = () => {
                /** There should be a message element to tell the user that something went wrong or right, after the {@link form} was submitted. */
                const successNotification: HTMLElement | null =
                  form.querySelector('.freeform-form-success');

                /** Set keyboard focus to the {@link notification} */
                /** Focus on the success message and add necessary WAI-ARIA attributes */
                if (successNotification) {
                  successNotification.tabIndex = -1;

                  setTimeout(() => {
                    successNotification.focus();
                    setTimeout(() => {
                      successNotification.scrollIntoView({
                        block: 'center'
                      });
                    }, 1000);
                  }, 100); // Chrome timeout
                }
              };

              /**
               * Do stuff when the form is not submitted due to errors
               */
              const handleErrorMessages = () => {
                // Stop the default field error rendering

                const elementsWithErrors: (
                  HTMLInputElement | HTMLSelectElement
                )[] = Array.from(
                  form.querySelectorAll(
                    'input.freeform-has-errors, textarea.freeform-has-errors, select.freeform-has-errors'
                  )
                );

                let firstErrorElement = null;

                if (!elementsWithErrors) return;

                /** Focus on Input field that have error message and add necessary WAI-ARIA attributes */
                elementsWithErrors.forEach((element) => {
                  const errorId: string = `error-${Math.floor(Math.random() * 999999999) + 1}`;

                  const inputErrorNotification =
                    element.parentElement?.querySelector('.freeform-errors') ||
                    element.parentElement?.parentElement?.querySelector(
                      '.freeform-errors'
                    );

                  if (!inputErrorNotification) return;

                  inputErrorNotification.setAttribute('id', errorId);
                  element.setAttribute('aria-describedby', errorId);

                  if (!firstErrorElement) {
                    firstErrorElement = element;
                  }
                });

                if (firstErrorElement) {
                  firstErrorElement.focus();
                }
              };

              /**
               * Set the autocomplete attribute on input fields (text, email, tel) based on the label text.
               * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete}
               */

              const handleAutoComplete = () => {
                const labelMappings: { [tokenKey: string]: string[] } = {
                  firstName: ['vorname', 'first name'],
                  lastName: ['nachname', 'last name'],
                  name: ['name', 'vollständiger name', 'full name'],
                  street: ['straße', 'street'],
                  postalCode: ['postleitzahl', 'plz', 'postal code'],
                  city: ['stadt', 'city'],
                  country: ['land', 'country']
                };

                const autoCompleteTokens: { [key: string]: string } = {
                  name: 'name',
                  firstName: 'given-name',
                  lastName: 'family-name',
                  email: 'email',
                  telephone: 'tel',
                  street: 'address-line1',
                  postalCode: 'postal-code',
                  city: 'address-level2',
                  country: 'country'
                };

                const inputFields: NodeListOf<HTMLInputElement> =
                  form.querySelectorAll(
                    'input[type="text"]:not([aria-hidden="true"]), input[type="email"]:not([aria-hidden="true"]),input[type="tel"]:not([aria-hidden="true"])'
                  );

                inputFields.forEach((inputField) => {
                  const labelElement = form.querySelector(
                    `label[for="${inputField.id}"]`
                  );

                  if (!labelElement) return;

                  const inputType = inputField.type;
                  const labelText =
                    labelElement.textContent?.toLowerCase().trim() || '';

                  if (inputType === 'email') {
                    inputField.setAttribute(
                      'autocomplete',
                      autoCompleteTokens.email
                    );
                    return;
                  }

                  if (inputType === 'tel') {
                    inputField.setAttribute(
                      'autocomplete',
                      autoCompleteTokens.telephone
                    );
                    return;
                  }

                  if (inputType === 'text') {
                    for (const [tokenKey, keywords] of Object.entries(
                      labelMappings
                    )) {
                      if (keywords.some((keyword) => labelText === keyword)) {
                        inputField.setAttribute(
                          'autocomplete',
                          autoCompleteTokens[tokenKey]
                        );
                        break;
                      }
                    }
                  }
                });
              };

              handleSuccessMessage();
              handleErrorMessages();
              handleAutoComplete();
            }
          }

          /**
           * Freeform produces a general error message above all form fields. Screenreaders would never read it, that is why we create an invisible error message that will politely add information to the screenreader output. Elegant and subtle.
           */
          const handleGeneralErrorMessage = () => {
            const generalErrorMessage = component.querySelector(
              '.freeform-form-errors'
            );
            if (!generalErrorMessage) return;

            const notification = sendNotification(
              generalErrorMessage.innerHTML
            );
            notification.classList.add('visuallyHidden');
          };

          handleGeneralErrorMessage();
        }, 100);
      };

      // Create an observer instance linked to the callback function
      const observer = new MutationObserver(callback);

      // Start observing the target node for configured mutations
      observer.observe(form, {
        childList: true,
        attributes: false,
        subtree: true
      });
    };

    handleStateChanges();
  });

  /**
   * Freeform Javascript Plugin
   * @see {@link https://docs.solspace.com/craft/freeform/v4/developer/js-plugin/}
   */
  const handleFreeformPlugin = () => {
    /** Listen for ready */
    document.addEventListener(
      'freeform-ready',
      function (event: FreeFormReadyEvent) {
        const handleRequiredFields = () => {
          const requiredFields: (HTMLInputElement | HTMLSelectElement)[] =
            Array.from(event.form.querySelectorAll('[data-required]'));

          requiredFields.forEach((field) => {
            /** The plugin Freeform uses custom error messages, therefore the `required` attribute is not present. We can add the `aria-required` to support screen reader users. */
            field.setAttribute('aria-required', 'true');

            /** Set the attribute `required` that Freeform fails to apply to radio elements (maybe also to other elements). */
            field.required = true;
          });
        };

        /**
         * Improve the accessibility of the drag-and-drop file upload field
         */
        const handleFileInputFields = () => {
          const fields: HTMLElement[] = Array.from(
            event.form.querySelectorAll(
              '[data-freeform-file-upload="fileDragAndDrop"]'
            )
          );

          fields.forEach((field) => {
            /** Add element to the tabindex */
            field.tabIndex = 0;

            field.addEventListener('keydown', (e: KeyboardEvent) => {
              /** The drag and drop file input field does not support Enter or Space key as it should. It does support click – so we fire click on Space or Enter keydown. */
              if (['Space', 'Enter'].includes(e.code)) {
                e.preventDefault();
                field.click();
              }
            });
          });
        };

        /**
         * We use „Friendly Captcha” as a captcha service. The `<form>` should not be submittable, unless the captcha has been solved. By default the `<form>` is submittable, because there is no real connection between Friendly Captcha and the `<form>`. That is why we disable the form submission by default and only enable it, once the captcha is solved.
         */
        const handleCaptcha = () => {
          /**
           * Block the submit event of a form
           * @param e Submit event
           */
          const blockSubmit = (e: SubmitEvent) => {
            e.preventDefault();
          };

          /** Elements that would trigger a submit event. We disable these so the user understands, that the form is not submittable. */
          const submitElements: (HTMLButtonElement | HTMLInputElement)[] =
            Array.from(event.form.querySelectorAll('[type="submit"]'));

          /** Block all submit functionality */
          const block = () => {
            event.form.addEventListener('submit', blockSubmit);
            submitElements.forEach((submitElement) => {
              submitElement.disabled = true;
            });
            event.form.setAttribute('aria-busy', 'true');
          };

          /** Unblock all submit functionality */
          const unblock = () => {
            event.form.removeEventListener('submit', blockSubmit);
            submitElements.forEach((submitElement) => {
              submitElement.disabled = false;
            });
            event.form.removeAttribute('aria-busy');
          };

          /** Block form */
          block();

          /** Listen for captcha solving */
          const captchaElement: HTMLElement =
            event.form.querySelector('.frc-captcha');
          if (captchaElement) {
            new WidgetInstance(event.form.querySelector('.frc-captcha'), {
              doneCallback: () => {
                /** Captcha is solved */
                unblock();
              }
            });
          }

          setTimeout(() => {
            unblock(); // unblock anyways in case the friendly-captcha widget fails
          }, 3000);
        };

        handleCaptcha();
        handleRequiredFields();
        handleFileInputFields();
      }
    );
  };
  handleFreeformPlugin();
};
